import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { Popover } from 'components/Popover/Popover'
import { DocumentPropertyValue } from 'api/clients/PropertyClient'
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState, MouseEventHandler } from 'react'
import styles from './DocPicker.module.scss'
import { useKey, useMeasure } from 'react-use'
import { useOverlayHack } from 'hooks/useOverlayHack'
import cx from 'classnames'
import { useDocs } from './useDocs'
import { ReactComponent as DocIcon } from 'assets/icons/document16.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close16.svg'
import { MentionableArticle } from 'types'
import { clamp } from 'lodash'
import {
  SelectPropertyEventDetails,
  selectPropertyEventName,
} from 'screens/Finder/components/FileTree/events/selectProperty'
import { getArticlePath } from 'utils/urlUtils'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { Text } from 'blaze-ui/Text/Text'

export type DocPickerProps = {
  isEditable?: boolean
  onChange: (value: MentionableArticle) => void
  onDelete?: (value: DocumentPropertyValue) => void
  selected: DocumentPropertyValue | undefined
  onEditingStateChange?: (isEditing: boolean) => void
}

export type DocPillProps = {
  title: string
  emoji: string | null
  onRemove?: () => void
  onDocClick: MouseEventHandler<HTMLDivElement>
}

export const DocPill: React.FC<React.PropsWithChildren<DocPillProps>> = ({ title, emoji, onRemove, onDocClick }) => {
  const isDismissible = !!onRemove

  const handleDocRemove: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onRemove?.()
  }

  return (
    <div
      data-testid="open-doc-button"
      className={cx(styles.DocPill, { [styles.dismissible]: isDismissible })}
      onClick={onDocClick}
    >
      <div className={styles.emoji}>{emoji ?? <DocIcon />}</div>
      <div className={styles.title}>{title}</div>
      {isDismissible && (
        <button data-testid="remove-doc-button" className={styles.removeButton} onClick={handleDocRemove}>
          <CloseIcon />
        </button>
      )}
    </div>
  )
}

export type DocPickerHandles = {
  focus: () => void
  setEditing: (editingState: boolean) => void
  toggleEditing: () => void
}

export const DocPicker = forwardRef<DocPickerHandles, DocPickerProps>(
  ({ isEditable, selected, onChange, onDelete, onEditingStateChange }, ref) => {
    const [isEditing, setIsEditing] = useState(false)
    const [inputSizerRef, { width: inputWidth }] = useMeasure<HTMLDivElement>()
    const [input, setInput] = useState('')
    const editableFieldRef = React.useRef<HTMLInputElement>(null)
    const selectedTagsContainerRef = React.useRef<HTMLDivElement>(null)
    const openWindowSafely = useOpenWindowSafely()
    const [containerRef, { width: containerWidth, height: containerHeight }] = useMeasure<HTMLDivElement>()
    useEffect(() => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      containerRef(selectedTagsContainerRef.current!)
    }, [containerRef, selectedTagsContainerRef])

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInput(e.currentTarget.value)
    }

    const handleDocClick: MouseEventHandler<HTMLDivElement | HTMLButtonElement> = (e) => {
      if (!selected?.value.key) return

      e.stopPropagation()
      e.preventDefault()
      const path = getArticlePath({ slug: selected.value.key })
      openWindowSafely(path)
    }

    const overlayRef = useRef<HTMLDivElement>(null)

    const docs = useDocs(input, isEditing)

    const { shouldFlipOverlayPlacement, positionPropsStyle } = useOverlayHack(
      overlayRef,
      selectedTagsContainerRef,
      isEditing,
      containerHeight + 8,
      0,
      docs,
    )

    const docsCount = docs?.length ?? 0
    const [highlightedOptionIdx, setHighlightedOptionIdx] = useState(0)
    useEffect(() => {
      if (docsCount === 0) {
        setHighlightedOptionIdx(0)
        return
      }
      setHighlightedOptionIdx(clamp(highlightedOptionIdx, 0, docsCount - 1))
    }, [docsCount, highlightedOptionIdx, input])

    useImperativeHandle(ref, () => ({
      focus: () => {
        setIsEditing(true)
        editableFieldRef.current?.focus()
      },
      setEditing: (editingState) => {
        setIsEditing(editingState)
      },
      toggleEditing: () => {
        setIsEditing((v) => !v)
      },
    }))

    useKey(
      'ArrowUp',
      (e) => {
        if (e.target !== editableFieldRef.current) return
        e.stopPropagation()
        e.preventDefault() // prevent going to the start of the input
        setHighlightedOptionIdx((idx) => {
          if (docsCount === 0) return 0
          return clamp(idx - 1, 0, docsCount - 1)
        })
      },
      undefined,
      [docsCount],
    )
    useKey(
      'ArrowDown',
      (e) => {
        if (e.target !== editableFieldRef.current) return
        e.stopPropagation()
        e.preventDefault() // prevent going to the end of the input
        setHighlightedOptionIdx((idx) => {
          if (docsCount === 0) return 0
          return clamp(idx + 1, 0, docsCount - 1)
        })
      },
      undefined,
      [docsCount],
    )

    useKey(
      'Backspace',
      (e) => {
        if (input.length === 0 && e.target === editableFieldRef.current) {
          handleClearSelection()
        }
      },
      undefined,
      [input],
    )

    useKey(
      'Tab',
      (e) => {
        if (e.target !== editableFieldRef.current) return
        // close dropdown
        setInput('')
        setHighlightedOptionIdx(0)
        setIsEditing(false)
        // closing the dropdown will focus its cell, so we queue the focus change event on the next tick
        setTimeout(() => {
          document.body.dispatchEvent(
            new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
              bubbles: false,
              detail: e.shiftKey ? 'Previous' : 'Next',
            }),
          )
        }, 0)
      },
      undefined,
      [editableFieldRef],
    )

    useKey(
      'Enter',
      (e) => {
        if (e.target !== editableFieldRef.current) return
        setInput('')
        setHighlightedOptionIdx(0)
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (docs![highlightedOptionIdx]) {
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          handleSelectDoc(docs![highlightedOptionIdx])()
        }
        document.body.dispatchEvent(
          new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
            bubbles: false,
            detail: 'Down',
          }),
        )
      },
      undefined,
      [input, highlightedOptionIdx, editableFieldRef, docs],
    )

    useEffect(() => {
      onEditingStateChange?.(isEditing)
    }, [isEditing, onEditingStateChange])

    const handleClearSelection = () => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      onDelete?.(selected!)
    }

    const handleSelectDoc = (doc: MentionableArticle) => () => {
      setIsEditing(false)
      setInput('')
      setHighlightedOptionIdx(0)
      onChange(doc)
    }

    return (
      <div
        className={styles.DocPicker}
        onClick={() => {
          isEditable && setIsEditing(true)
        }}
        ref={selectedTagsContainerRef}
      >
        <button className={styles.selectedDocuments} onClick={handleDocClick}>
          {selected && (
            <DocPill title={selected.value.title} emoji={selected.value.header_emoji} onDocClick={handleDocClick} />
          )}
        </button>
        {isEditing && (
          <FloatingPortal id="floating-ui-root">
            <Popover
              style={positionPropsStyle}
              isOpen={true}
              onClose={() => setIsEditing(false)}
              ref={overlayRef}
              isDismissable
              className={cx(styles.popover, { [styles.flipped]: shouldFlipOverlayPlacement })}
            >
              <div
                className={styles.selectedDocDisplay}
                style={{ width: containerWidth + 16, minHeight: containerHeight + 8 }}
                onClick={() => {
                  editableFieldRef.current?.focus()
                }}
              >
                {selected && (
                  <DocPill
                    title={selected.value.title}
                    emoji={selected.value.header_emoji}
                    onRemove={handleClearSelection}
                    onDocClick={handleDocClick}
                  />
                )}
                {!selected && input.length === 0 && (
                  <Text variant="secondary" color="dark-40">
                    Search or select a doc
                  </Text>
                )}
                <div className={styles.sizer} ref={inputSizerRef} aria-hidden={true}>
                  {input}
                </div>
                <input
                  type="text"
                  className={styles.editableField}
                  value={input}
                  onChange={handleInput}
                  ref={editableFieldRef}
                  style={{ width: inputWidth + 30 }}
                  autoFocus
                />
              </div>

              {docsCount > 0 && (
                <div className={styles.dropdown} style={{ maxWidth: Math.max(200, containerWidth + 16) }}>
                  <ul className={styles.eligibleDocs}>
                    {docs?.map((doc, idx) => (
                      <li
                        key={idx}
                        className={cx(styles.option, {
                          [styles.highlighted]: idx === highlightedOptionIdx,
                        })}
                        onClick={handleSelectDoc(doc)}
                      >
                        <DocPill title={doc.title} emoji={doc.header_emoji} onDocClick={handleDocClick} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Popover>
          </FloatingPortal>
        )}
      </div>
    )
  },
)
DocPicker.displayName = 'DocPicker'
